import React, { useState, useEffect, useRef, createElement } from 'react'
import { createPortal } from 'react-dom'
import theme from '../config/theme'
import { IoEllipsisVertical, IoCreateOutline, IoTrashOutline } from 'react-icons/io5'

export interface ActionMenuProps {
  itemId: string
  onEdit: (id: string) => void
  onDelete: (id: string) => void
  className?: string
  disableDelete?: boolean
}

const ActionMenu: React.FC<ActionMenuProps> = ({
  itemId,
  onEdit,
  onDelete,
  className = '',
  disableDelete = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 })

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect()
      const scrollX = window.pageXOffset || document.documentElement.scrollLeft
      const scrollY = window.pageYOffset || document.documentElement.scrollTop
      const dropdownWidth = 192 // w-48 = 12rem = 192px
      
      setDropdownPosition({
        top: buttonRect.bottom + scrollY + 8, // 8px = mt-2 spacing
        left: buttonRect.right + scrollX - dropdownWidth // Align to right of button
      })
    }
  }, [isOpen])

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsOpen(!isOpen)
  }

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsOpen(false)
    onEdit(itemId)
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsOpen(false)
    onDelete(itemId)
  }

  return (
    <>
      <div 
        className={`relative ${className}`}
      >
        <button
          ref={buttonRef}
          onClick={handleToggle}
          className="focus:outline-none transition-colors"
          style={{ color: theme.color.gray100 }}
        >
          {createElement(IoEllipsisVertical as any, { className: "w-5 h-5" })}
        </button>
      </div>
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="fixed w-48 rounded-md shadow-lg z-[9999] border"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            backgroundColor: theme.color.light100,
            borderColor: theme.color.light300,
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="py-1">
            <button
              onClick={handleEdit}
              className="flex items-center px-4 py-2 text-sm w-full text-left transition-colors hover:bg-gray-100"
              style={{ color: theme.color.gray500 }}
            >
              {createElement(IoCreateOutline as any, { className: "w-4 h-4 mr-3" })}
              Edit
            </button>
            <button
              onClick={handleDelete}
              disabled={disableDelete}
              className={`flex items-center px-4 py-2 text-sm w-full text-left transition-colors ${
                disableDelete ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-100'
              }`}
              style={{ color: disableDelete ? theme.color.gray200 : theme.color.error }}
            >
              {createElement(IoTrashOutline as any, { className: "w-4 h-4 mr-3" })}
              Delete
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

export default ActionMenu

